import React, { useState, useEffect } from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import CheckSharpIcon from "@mui/icons-material/CheckSharp";
import { fetchData } from "../services/api";
import apiUrls from "../config/apiUrls";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import TripleToggleButton from "../ui/ToggleButton/TripleToggleButton";
import RadioButton from "../ui/Radio/Radio";
import SelectDropdown from "../ui/SelectDropdown";
import CustomButton from "../ui/Button";
import CustomIconButton from "../ui/IconButton";
import handleApiResponse from "../utils/handleApiResponse";
import ApiResponse from "../types/IApiResponse";
import { IOption } from "../types/IOption";

interface Team {
  value: string;
  label: string;
}

interface FilterOptions {
  homeTeam: string;
  matchLabel: string;
  matchStatus: string;
  selectedOption: string;
  competitionId?: number;
  seasonId?: number;
  selectedSeason?: string;
}

interface MatchSelectionProps {
  onCloseDrawer: () => void;
  onFilterChange: (filterName: string, value: string | boolean) => any;
  selectedFilters: FilterOptions;
}

const names: string[] = [
  "Oliver Hansen",
  "Van Henry",
  "April Tucker",
  "Ralph Hubbard",
  "Omar Alexander",
  "Carlos Abbott",
  "Miriam Wagner",
  "Bradley Wilkerson",
  "Virginia Andrews",
  "Kelly Snyder",
];

const matchLabels = [
  { key: "1", value: "ALL" },
  { key: "2", value: "HOME" },
  { key: "3", value: "AWAY" },
];

const matchStatus = [
  { key: "1", value: "ALL" },
  { key: "2", value: "WON" },
  { key: "3", value: "LOST" },
];

const MatchSelection: React.FC<MatchSelectionProps> = ({ onCloseDrawer, onFilterChange, selectedFilters }) => {
  const [teams, setTeams] = useState<IOption[]>([]);
  const [competitions, setCompetitions] = useState<IOption[]>([]);
  const [seasons, setSeasons] = useState<IOption[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>("");
  const [selectedCompetitions, setSelectedCompetitions] = useState<string>("");
  const [selectedSeason, setSelectedSeason] = useState<string>("");
  const [selectedMatchLabel, setSelectedMatchLabel] = useState<string>("1");
  const [selectedMatchStatus, setSelectedMatchStatus] = useState<string>("1");
  const [selectedOption, setSelectedOption] = useState<string>("LAST MATCH");
  const [selectedOpposition, setSelectedOpposition] = useState<string>("All");

  const handleConfirm = () => {
    onCloseDrawer();
    onFilterChange("homeTeam", selectedTeam);
    onFilterChange("matchLabel", selectedMatchLabel);
    onFilterChange("matchStatus", selectedMatchStatus);
    onFilterChange("selectedOption", selectedOption);
    onFilterChange("selectedOppositionTeamId", selectedOpposition);
    onFilterChange("competitionId", selectedCompetitions);
    onFilterChange("seasonId", selectedSeason);
  };

  const handleSelectChange = (event: any) => {
    setSelectedTeam(event.target.value);
    const { value } = event.target;
  };

  const handleSelectCompetitionChange = (event: any) => {
    setSelectedCompetitions(event.target.value);
    setSelectedSeason("");
    setSelectedTeam("");
  };

  const handleSelectSeasonChange = (event: any) => {
    const seasonName = seasons.filter((a) => a.value === event.target.value)[0].label;
    setSelectedSeason(event.target.value);
    setSelectedOption(seasonName);
  };

  useEffect(() => {
    const fetchCompetitionData = async () => {
      const response: ApiResponse<Team[]> = await fetchData(apiUrls["GetCompetition"]);
      handleApiResponse(
        response,
        (competitionData) => {
          setCompetitions(competitionData);
        },
        (error) => {
          console.error("Error fetching Competiton Data:", error);
        }
      );
    };
    fetchCompetitionData();
  }, []);

  useEffect(() => {
    const fetchTeamData = async () => {
      if (selectedCompetitions !== "") {
        const response: ApiResponse<Team[]> = await fetchData(`${apiUrls["HomeTeamData"]}?CompetitionId=${selectedCompetitions}`);
        handleApiResponse(
          response,
          (teamsData) => {
            setTeams(teamsData);
          },
          (error) => {
            console.error("Error fetching Home Team Data:", error);
          }
        );
      }
    };
    fetchTeamData();
  }, [selectedCompetitions]);

  useEffect(() => {
    const fetchSeasonData = async () => {
      if (selectedCompetitions !== "") {
        const response: ApiResponse<Team[]> = await fetchData(`${apiUrls["GetCompetitionSeason"]}?CompetitionId=${selectedCompetitions}`);
        handleApiResponse(
          response,
          (seasonData) => {
            setSeasons(seasonData);
          },
          (error) => {
            console.error("Error fetching Season Data:", error);
          }
        );
      }
    };
    fetchSeasonData();
  }, [selectedCompetitions]);

  const handleOptionClick = (option: string) => {
    setSelectedOption(option);
  };

  return (
    <div className="last-matches-modal">
      <div className="modal-header">
        <h5>MATCH SELECTION</h5>
        <CustomIconButton onClick={onCloseDrawer}>
          <HighlightOffIcon />
        </CustomIconButton>
      </div>
      <div className="model-body">
        <div className="modal-inner-wrapper">
          <div className="dropdown-here">
            <span>COMPETITIONS</span>
            <List>
              <SelectDropdown
                label="Competitions"
                options={competitions}
                value={selectedCompetitions}
                onChange={handleSelectCompetitionChange}
                multiple={false} // Set to false for single selection
                selectAllOption={false} // Optionally show "Select All" option
                required={true}
              />
            </List>
          </div>
          <span>TIMEFRAME</span>
          <List>
            {["LAST MATCH", "THIS SEASON"].map(
              (
                option // 'SET TIME FRAME'
              ) => (
                <ListItem key={option} style={{ border: "1px solid GrayText", marginBottom: "20px" }}>
                  <RadioButton selected={selectedOption === option} onChange={() => handleOptionClick(option)} label={option} />
                  <ListItemText primary={option} />
                  {option === "THIS SEASON" && (
                    <>
                      <TripleToggleButton
                        labels={matchLabels}
                        defaultLabelKey={selectedMatchLabel}
                        onChange={(selectedLabel) => setSelectedMatchLabel(selectedLabel.key)}
                      />
                      <TripleToggleButton
                        labels={matchStatus}
                        defaultLabelKey={selectedMatchStatus}
                        onChange={(selectedLabel) => setSelectedMatchStatus(selectedLabel.key)}
                      />
                    </>
                  )}
                </ListItem>
              )
            )}
            <div className="dropdown-here">
              <List>
                <SelectDropdown
                  label="Seasons"
                  options={seasons}
                  value={selectedSeason}
                  onChange={handleSelectSeasonChange}
                  multiple={false} // Set to false for single selection
                  selectAllOption={false} // Optionally show "Select All" option
                  isSearchDropdown={false}
                />
              </List>
            </div>
          </List>
          <div className="dropdown-here">
            <span>MATCHES</span>
            <List>
              <SelectDropdown
                label="Home Team"
                options={teams}
                value={selectedTeam}
                onChange={handleSelectChange}
                multiple={false} // Set to false for single selection
                selectAllOption={false} // Optionally show "Select All" option
              />
              <SelectDropdown
                label="ALL OPPOSITION"
                options={teams}
                value={selectedOpposition}
                onChange={(event: any) => {
                  setSelectedOpposition(event.target.value);
                }}
                multiple={false} // Set to false for single selection
                selectAllOption={false} // Optionally show "Select All" option
                noSelectionOption={true}
              />
            </List>
          </div>
        </div>
      </div>
      <div className="modal-footer">
        <CustomButton startIcon={<CheckSharpIcon />} onClick={handleConfirm}>
          CONFIRM
        </CustomButton>
      </div>
    </div>
  );
};

export default MatchSelection;
